// TODO update this to import from commons
// import { gql } from '@reverbdotcom/commons/src/gql';
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';
import React from 'react';
import { SignalsData } from '@reverbdotcom/commons/src/gql/graphql';
import ListingNudge from './discovery/listing_nudge';
import { RCTooltip } from '@reverbdotcom/cadence/components';
import OrangeSaleTag from '@reverbdotcom/cadence/images/nudge/orange-sale-tag.svg';
import I18n from 'i18n-js';
import { REVERB_OUTLET_PATH } from '@reverbdotcom/commons/src/url_helpers';
import { useExpEnabled, useUser } from '@reverbdotcom/commons/src/user_hooks';
import experiments from '@reverbdotcom/commons/src/experiments';
import { isEU } from '@reverbdotcom/commons/src/geo';

export const ICON_HEIGHT_TINY = 20;
export const ICON_HEIGHT_REGULAR = 34;

export const SignalsFragment = gql`
  fragment SignalsData on Listing {
    _id
    id
    usOutlet
    currency
    signals: signals (input: { groups: [ITEM_PAGE_WHY_TO_BUY, ITEM_PAGE_NUDGES, ITEM_PAGE_WHY_REVERB] }) {
      name
      group
      title
      subtitle
      link {
        text
        url
      }
      icon
      tooltipText
    }
  }
`;

type OutletCallout = {
  name: string,
  icon: string,
  title: string,
  subtitle: JSX.Element,
  tooltipText?: string,
  link?: {
    text?: string,
    url?: string
  }
};

export function Item2WhyReverbSignals({ listing }: SignalsProps) {
  const signals = listing.signals?.filter(signal => signal.group === 'ITEM_PAGE_WHY_REVERB');

  if (!signals?.length) { return null; }

  return (
    <div className="item2__content__section item2__content__section--why-reverb">
      <div className="item2__sidebar-module item2__sidebar-module--shaded">
        {signals.map(signal => {
          return <SignalDisplay signal={signal} inline={false} key={signal.name} />;
        })}
      </div>
    </div>
  );
}

export function Item2NudgeSignals({ listing }: SignalsProps) {
  let signals;

  signals = listing.signals?.filter(signal => signal.group === 'ITEM_PAGE_NUDGES');
  const greatValueBadgeEuExpEnabled = useExpEnabled(experiments.GREAT_VALUE_BADGE_EU);
  const { shippingRegionCode } = useUser();

  if (!greatValueBadgeEuExpEnabled && isEU(shippingRegionCode) && listing.currency === 'EUR') {
    signals = signals.filter(signal => signal.name !== 'GREAT_VALUE');
  }

  if (!signals?.length) { return null; }

  return (
    <div className="item2__sidebar-module">
      {signals.map(signal => {
        return <SignalDisplay signal={signal} inline={false} key={signal.name} />;
      })}
    </div>
  );
}

export function Item2OutletCallout({ listing }: SignalsProps) {
  if (!listing.usOutlet) { return null; }

  const outletCallout: OutletCallout = {
    name: 'outlet',
    icon: OrangeSaleTag,
    title: I18n.t('discovery.outletCallout.title'),
    subtitle: I18n.t('discovery.outletCallout.subtitle'),
    link: {
      text: I18n.t('discovery.outletCallout.linkText'),
      url: REVERB_OUTLET_PATH,
    },
  };

  return (
    <SignalDisplay signal={outletCallout} inline={false} key={outletCallout.name} linkInline />
  );
}

interface SignalsProps {
  listing: SignalsData.Fragment
}

export function Item2WhyToBuySignals({ listing }: SignalsProps) {
  const signals = listing.signals?.filter(signal => signal.group === 'ITEM_PAGE_WHY_TO_BUY');

  if (!signals?.length) { return null; }

  return (
    <div className="mtb-2">
      {signals.map(signal => {
        return <SignalDisplay signal={signal} inline key={signal.name} />;
      })}
    </div>
  );
}

interface Signal {
  name?: string,
  group?: string,
  title?: string,
  subtitle?: string,
  tooltipText?: string,
  link?: {
    text?: string,
    url?: string
  },
  icon?: string
}

function SignalDisplay({ signal, inline, linkInline = false }: { signal: Signal | OutletCallout, inline: boolean, linkInline?: boolean }) {
  if (!signal.name || !signal.icon) {
    return null;
  }

  const getIconSvg = function () {
    const iconHeight = inline ? ICON_HEIGHT_TINY : ICON_HEIGHT_REGULAR;
    return (
      <svg height={iconHeight}>
        <image href={signal.icon} height={iconHeight} />
      </svg>
    );
  };

  const subtitle = signal.subtitle && signal.tooltipText ?
    <RCTooltip text={signal.tooltipText} type="underline">
      {signal.subtitle}
    </RCTooltip> :
    <>{signal.subtitle}</>;

  const title = !signal.subtitle && signal.tooltipText ?
    <RCTooltip text={signal.tooltipText} type="underline">
      {signal.title}
    </RCTooltip> :
    <>{signal.title}</>;

  return (
    <ListingNudge
      title={title}
      subtitle={subtitle}
      linkText={signal.link?.text}
      linkUrl={signal.link?.url}
      inline={inline}
      icon={getIconSvg()}
      key={signal.title}
      linkInline={linkInline}
    />
  );
}
